import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { apiService } from '../services/api';
import {
  LayoutDashboard,
  Users,
  FileText,
  Download,
  Settings,
  LogOut,
  GraduationCap,
  UserCog,
  Upload,
  Building2,
  ChevronDown,
  ClipboardCheck,
  Contact
} from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'School Admin',
  specialist: 'Interventionist',
  viewer: 'Viewer'
};

export default function Sidebar() {
  const { 
    user, 
    logout, 
    canEdit, 
    canManageUsers, 
    canAccessSettings, 
    canManageSchools,
    isSuperAdmin,
    selectedSchoolId,
    setSelectedSchoolId,
    userSchoolName
  } = useAuth();
  const { showAssessmentsNav, isAssessmentsComingSoon } = useFeatureFlags();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      loadSchools();
    }
  }, [isSuperAdmin]);

  const loadSchools = async () => {
    try {
      const response = await apiService.getSchools();
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to load schools:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSchoolChange = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setShowSchoolDropdown(false);
  };

  const getSelectedSchoolName = () => {
    if (!selectedSchoolId) return 'All Schools';
    const school = schools.find(s => s.id === selectedSchoolId);
    return school ? school.name : 'All Schools';
  };

  // Build nav items based on role
  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/students', icon: Users, label: 'Students', show: true },
    { path: '/import', icon: Upload, label: 'Import', show: canEdit },
    { path: '/reports', icon: FileText, label: 'Reports', show: true },
    { path: '/exports', icon: Download, label: 'Exports', show: canEdit },
    { 
      path: '/assessments', 
      icon: ClipboardCheck, 
      label: 'Assessments', 
      show: showAssessmentsNav(),
      badge: isAssessmentsComingSoon() ? 'Coming Soon' : null
    },
    { path: '/staff', icon: Contact, label: 'Staff', show: canManageUsers },
    { path: '/users', icon: UserCog, label: 'Users', show: canManageUsers },
    { path: '/schools', icon: Building2, label: 'Schools', show: canManageSchools },
    { path: '/settings', icon: Settings, label: 'Settings', show: canAccessSettings }
  ];

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            <GraduationCap size={20} />
          </div>
          <div className="flex flex-col">
            <span className="sidebar-logo-text">Frame Interventions</span>
            <span className="text-xs text-[#595959] font-normal">MTSS Progress Tracker</span>
          </div>
        </div>
      </div>

      {/* School Selector for Super Admin */}
      {isSuperAdmin && schools.length > 0 && (
        <div className="px-3 py-2 border-b border-[#E5E5E5]">
          <div className="relative">
            <button
              onClick={() => setShowSchoolDropdown(!showSchoolDropdown)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm bg-[#F3F4F1] hover:bg-[#E5E5E5] rounded-xl transition-colors"
              data-testid="school-selector"
            >
              <div className="flex items-center gap-2 truncate">
                <Building2 size={16} className="text-[#595959] flex-shrink-0" />
                <span className="truncate text-[#1C1C1C]">{getSelectedSchoolName()}</span>
              </div>
              <ChevronDown size={16} className={`text-[#595959] transition-transform ${showSchoolDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showSchoolDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                <button
                  onClick={() => handleSchoolChange(null)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F3F4F1] rounded-t-xl ${!selectedSchoolId ? 'bg-[#2C4C3B]/10 text-[#2C4C3B] font-medium' : ''}`}
                >
                  All Schools
                </button>
                {schools.map(school => (
                  <button
                    key={school.id}
                    onClick={() => handleSchoolChange(school.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F3F4F1] truncate ${selectedSchoolId === school.id ? 'bg-[#2C4C3B]/10 text-[#2C4C3B] font-medium' : ''}`}
                  >
                    {school.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* School Name Display for Regular Users */}
      {!isSuperAdmin && userSchoolName && (
        <div className="px-3 py-2 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2 px-3 py-2 text-sm bg-[#F3F4F1] rounded-xl">
            <Building2 size={16} className="text-[#595959]" />
            <span className="truncate text-[#1C1C1C]">{userSchoolName}</span>
          </div>
        </div>
      )}

      <nav className="sidebar-nav">
        {navItems.filter(item => item.show).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon size={20} />
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B] whitespace-nowrap">
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.name || 'User'}</div>
            <div className="user-email" style={{ fontSize: '11px' }}>
              {ROLE_LABELS[user?.role] || 'Viewer'} • {user?.email || ''}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="btn btn-ghost btn-icon"
            title="Sign out"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}
